import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ModernAuth } from '@/components/ModernAuth';
import { AccountCreation } from '@/components/AccountCreation';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Signin = () => {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');

  // CSS variable trick for iOS Safari 100vh issues
  useEffect(() => {
    const setViewportHeight = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    return () => window.removeEventListener('resize', setViewportHeight);
  }, []);

  // Mock initial data for returning users (they don't need to fill this out again)
  const mockInitialData = {
    name: '',
    age: '',
    state: '',
    coverageAmount: '',
    smokingStatus: '',
  };

  const handleAuthSuccess = (user: any) => {
    navigate('/dashboard');
  };

  const handleAccountCreated = (user: any) => {
    navigate('/onboarding');
  };

  return (
    <main className="w-full min-h-[100dvh] min-h-[calc(var(--vh,1vh)*100)] overflow-y-auto scrollable bg-background">
      {/* Sticky header with tabs + back button */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="flex items-center justify-between px-4 py-3 max-w-md mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="p-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <div className="inline-flex rounded-xl bg-muted p-1">
            <Button
              variant={authMode === 'signin' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setAuthMode('signin')}
              className="px-3 py-1.5 rounded-lg text-sm"
            >
              Sign In
            </Button>
            <Button
              variant={authMode === 'signup' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setAuthMode('signup')}
              className="px-3 py-1.5 rounded-lg text-sm"
            >
              Create Account
            </Button>
          </div>
          
          <div className="w-8" /> {/* Spacer for symmetry */}
        </div>
      </div>

      {/* Content section that can scroll */}
      <section className="px-4 py-6 max-w-md mx-auto">
        <div className="space-y-6">
          <div className="text-center">
            <img 
              src="/lovable-uploads/0a3243f2-c9d9-43be-8f37-54dd1750981b.png" 
              alt="Safurra" 
              className="h-12 w-12 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {authMode === 'signin' ? 'Welcome Back' : 'Join Safurra'}
            </h1>
            <p className="text-muted-foreground">
              {authMode === 'signin' ? 'Sign in to your account' : 'Create your account to get started'}
            </p>
          </div>

          {authMode === 'signin' ? (
            <ModernAuth 
              initialData={mockInitialData}
              onAuthSuccess={handleAuthSuccess}
              mode="signin"
              hideModeToggle={true}
            />
          ) : (
            <AccountCreation 
              initialData={mockInitialData}
              onAccountCreated={handleAccountCreated}
            />
          )}
        </div>
      </section>
      
      {/* Bottom breathing room */}
      <div className="h-10" />
    </main>
  );
};

export default Signin;